// The value every sales figure is shown at — the value-basis "master filter" (owner, 1 Nov 2026: "In LEO CORE I need
// MRP-Incl GST, NET Sales - INCL GST, NRV-EXCL GST — this master filter, with a small description of what is included
// and excluded in each"). The names, wording and rules mirror RetailMaxx (src/shared/basis.ts), so a figure reads the same
// in both applications; LEO CORE offers three of RetailMaxx's four settings (no "MRP value excl. GST").
//
// Net realised value is what Leo keeps from a sale — NOT the stock valuation "net realisable value" of Ind AS 2, which the
// help text says. NRV is the default: it is what LEO CORE showed before the choice existed (sales.fact.net, taxable value
// before GST), so nothing changes until someone picks another setting.
export const VALUE_BASES = ['mrp', 'gst', 'nrv'] as const;
export type ValueBasis = (typeof VALUE_BASES)[number];
/** Shown to everyone unless they choose another (what LEO CORE showed before the choice existed). */
export const DEFAULT_VALUE_BASIS: ValueBasis = 'nrv';
/** The query parameter every sales request carries when the setting is not the default (the API cache is keyed by URL). */
export const VALUE_BASIS_PARAM = 'basis';
/** The personal setting it is kept under (idm.pref, migration 0027): it follows the person to any browser. */
export const VALUE_BASIS_PREF = 'value.basis';
export const isValueBasis = (x: unknown): x is ValueBasis => typeof x === 'string' && (VALUE_BASES as readonly string[]).includes(x);
export const valueBasisOf = (x: unknown): ValueBasis => isValueBasis(x) ? x : DEFAULT_VALUE_BASIS;

export interface ValueBasisWords {
  /** The setting's name, as in RetailMaxx. */
  name: string;
  /** One line under the choice in the picker (RetailMaxx `brief`, owner 1 Nov 2026). */
  brief: string;
  /** A few words for a column header or a tag. */
  short: string;
  /** What the figure is, in one or two sentences (RetailMaxx's `means`). */
  means: string;
  /** What is in the figure (short phrases, for the picker and the help). */
  includes: string[];
  /** What is not in it: taken off, or left out. */
  excludes: string[];
  /** In a sentence: "net realised value (excl. GST)". */
  words: string;
}
export const VALUE_BASIS: Record<ValueBasis, ValueBasisWords> = {
  mrp: {
    name: 'MRP value (incl. GST)', short: 'MRP value', words: 'MRP value (incl. GST)',
    brief: 'Full price before discounts, less returns, with GST.',
    means: 'Units sold × MRP, less returns. Gross sales before any discount, GST included.',
    // discounts are NOT taken off on this setting: they are in the figure, so they are listed as included, never as excluded
    includes: ['Units sold at the full MRP (the printed price)', 'Discounts (not taken off)', 'GST'],
    excludes: ['Returns', 'Sales with no MRP on record (dealer invoices without item lines, credit notes): left out and disclosed beside the figure, never counted as zero'],
  },
  gst: {
    name: 'Net sales (incl. GST)', short: 'Net incl. GST', words: 'net sales (incl. GST)',
    brief: 'After discounts and returns, with GST. What customers paid.',
    means: 'What customers paid: MRP value less discounts and returns, GST included. The bill / invoice value.',
    includes: ['The bill / invoice value after discounts', 'GST'],
    excludes: ['Discounts', 'Returns and credit notes'],
  },
  nrv: {
    name: 'Net realised value (excl. GST)', short: 'NRV', words: 'net realised value (excl. GST)',
    brief: 'After discounts and returns, without GST. What Leo keeps.',
    means: 'What Leo keeps from a sale: MRP value less discounts, returns and GST (for dealers: the invoice before GST, less credit notes).',
    includes: ['The bill / invoice value after discounts, before GST'],
    excludes: ['GST', 'Discounts', 'Returns and credit notes'],
  },
};
/** NRV is not the Ind AS 2 stock valuation: said wherever the full description is. */
export const NRV_NOT_IND_AS2 = 'Net realised value is what Leo keeps from a sale — not the stock valuation “net realisable value” of Ind AS 2.';
/** How the three tie together, said once wherever the choice is offered (RetailMaxx BASIS_TIES, for LEO CORE's three). */
export const VALUE_BASIS_TIES = 'Discount = MRP value incl. GST − net sales incl. GST (its % is the same on every setting). GST = net sales incl. GST − NRV.';
const lc = (x: string) => /^[A-Z][a-z]/.test(x) ? x[0]!.toLowerCase() + x.slice(1) : x;   // "Returns" → "returns"; "GST" stays
const shortPhrase = (x: string) => lc(x.startsWith('Discounts (') ? x : x.split(':')[0]!.split(' (')[0]!);
/** The short line under each choice in the picker: what is in, what is out ("Includes: … · Excludes: …"). */
export const valueBasisShort = (b: ValueBasis): string => `Includes: ${VALUE_BASIS[b].includes.map(shortPhrase).join(', ')} · Excludes: ${VALUE_BASIS[b].excludes.map(shortPhrase).join(', ')}`;
/** The full description, for the help / explain drawer. */
export const valueBasisFull = (b: ValueBasis): string => `${VALUE_BASIS[b].means} ${VALUE_BASIS_TIES}${b === 'nrv' ? ` ${NRV_NOT_IND_AS2}` : ''}`;

/** The GST basis a plan version's targets are on (plan.version.basis), as a value basis. Targets are never set at MRP. */
export type TargetBasis = 'incl_gst' | 'net_ex_gst';
export const targetValueBasis = (b: string | null | undefined): ValueBasis | null => b === 'incl_gst' ? 'gst' : b === 'net_ex_gst' ? 'nrv' : null;
/**
 * Said beside every target: what the targets are on and what they are compared with — always like with like (the targets'
 * own GST basis), whatever values are shown (owner, 1 Nov 2026: the TARGET AND LEDGER targets are incl. GST).
 */
export function targetsCompareWords(planBasis: string | null | undefined, shown?: ValueBasis): string {
  const t = targetValueBasis(planBasis);
  if (!t) return 'The GST basis of the targets is not confirmed: they are not compared with sales until it is.';
  const set = t === 'gst' ? 'Targets are set incl. GST and compared with net sales incl. GST — like with like' : 'Targets are set excl. GST and compared with net realised value (excl. GST) — like with like';
  return shown && shown !== t ? `${set}, whatever values are shown (the other figures here are ${VALUE_BASIS[shown].name}).` : `${set}.`;
}

/**
 * Screens and figures that keep their own values whatever is chosen, and why — each says so on screen in one line
 * (RetailMaxx KEEPS_DATA_BASIS). Keys are screens. Never a word about cost or margin: these lines are shown to people who may
 * not see costs (a test holds it).
 */
export const KEEPS_OWN_BASIS = {
  finance: { tag: 'Values as in the accounts', why: 'The P&L and finance lines keep their own values: sales before GST, and GST on lines of their own.' },
  receivables: { tag: 'Money owed, incl. GST', why: 'Receivables and collections are money owed and paid, including GST, whatever values are shown.' },
  stock: { tag: 'Stock values as held', why: 'Stock is counted in units and stock values, whatever sales values are shown.' },
  health: { tag: 'Data as loaded', why: 'Data health and tie-outs compare the files as loaded, whatever values are shown.' },
  plan: { tag: 'Targets as set', why: 'Plan against actual compares the targets with sales on the targets’ own GST basis (like with like), whatever values are shown.' },
  decisions: { tag: 'Worked out once', why: 'Decisions and proposals are worked out once, on their own figures (units and stock values), so nothing recommended or measured changes with the values shown.' },
  orders: { tag: 'Orders at wholesale price', why: 'Orders and the invoices raised against them are at wholesale price before GST, whatever values are shown.' },
  market: { tag: 'Sales incl. GST, on the tracker’s basis', why: 'Competition compares our sales incl. GST with the Competitor Tracker’s figures, on the same basis, whatever values are shown.' },
  buying: { tag: 'As ordered', why: 'Purchase orders and commitments keep the values they were placed at, whatever sales values are shown.' },
} as const satisfies Record<string, { tag: string; why: string }>;
export type KeptScreen = keyof typeof KEEPS_OWN_BASIS;
